/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectConfig.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Chip,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";

import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import { CustomImportButton } from "../../../components";

import DenseViewBox from "../../../components/customComponentsV2/projectConfig/DenseViewBox";
import CompactViewBox from "../../../components/customComponentsV2/projectConfig/CompactViewBox";
import ViewProjectConfig from "./ViewProjectConfig";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import {
  adminSettingsAPI,
  HandleApiActions,
  profileActions,
  projectConfigAPI,
  settingsApis,
} from "../../../redux/actions";

import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import AddDialog from "../../../components/customComponentsV2/projectConfig/AddDialog";

import dayjs from "dayjs";
import CustomDatePicker from "../../../components/customComponentsV2/common/datepicker/CustomDatePicker";
import CustomFilterV2 from "../../../components/customComponentsV2/common/filter/CustomFilterV2";

import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import moment from "moment";
import InfoButton from "../../../components/infoButton/InfoButton";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import { GetSingleImages, showToastMessage } from "../../../redux/actions/actionHandler";
import CustomRadioButtonV3 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV3";
import CustomFormTextFieldV3 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import CustomSelectV3 from "../../../components/customComponentsV2/common/select/CustomSelectV3";
import CustomDatePickerV2 from "../../../components/customComponentsV2/common/datepicker/CustomDatePickerV2";
import { ReactComponent as GridIcon } from "../../../assets/logoIcons/grid__l__stroke.svg";
import { ReactComponent as ListIcon } from "../../../assets/logoIcons/list__l__stroke.svg";
import CustomCheckboxMultiSelect from "../../../components/customComponentsV2/common/multiselect/CustomCheckboxMultiSelect";

import { toast } from "react-toastify";
import sortUp from "../../../assets/sortup.png";
import sortDown from "../../../assets/sortdown.png";

const mapUserOptions = (users = []) =>
  users.map((item) => ({
    label: item.name,
    subLabel: item.email,
    value: item?.user_id,
  }));

const buildZoneOptions = (projectZones = []) =>
  projectZones.map((data) => ({
    label: data?.zone_name,
    value: data?.position,
    sequence: data?.sequence,
  }));

const buildDeliveryMarketOptions = (markets = []) =>
  markets.map((market) => ({
    label: market,
    value: market,
  }));

const buildAbbreviatedOptions = (items = []) =>
  items.map((item) => ({
    label: `${item?.name} (${item?.abbreviation})`,
    value: item?.id,
  }));

const buildVehicleFunctionOptions = buildAbbreviatedOptions;
const buildPowertrainClassOptions = buildAbbreviatedOptions;

const buildManufacturerOptions = (items = []) =>
  items.map((item) => ({
    label: item?.name,
    subLabel: item?.address,
    value: item,
  }));

const buildRecreateProjectState = (
  parentProjectDetails,
  countryData,
  milestoneNameOptions,
) => {
  const parentZoneNameDetails = buildZoneOptions(
    parentProjectDetails?.project_zones || [],
  );

  const parentModelYear = parentProjectDetails?.model_year
    ? dayjs(`${parentProjectDetails?.model_year}-01-01`)
    : null;

  const parentCountryData = countryData.find(
    (item) =>
      item.country?.toLowerCase() ===
      parentProjectDetails?.country?.toLowerCase(),
  );

  const parentDeliveryMarketDetails = buildDeliveryMarketOptions(
    parentProjectDetails?.delivery_market || [],
  );

  const parentTargetMarket = parentProjectDetails?.target_market;

  const parentVehicleFunctionDetails = buildVehicleFunctionOptions(
    parentProjectDetails?.vehicle_function_details || [],
  );

  const parentPowerTrainClassDetails = buildPowertrainClassOptions(
    parentProjectDetails?.powertrain_class_details || [],
  );

  const parentMftDetails = buildManufacturerOptions(
    parentProjectDetails?.mft_details || [],
  );

  const parentOwnerDetails = parentProjectDetails?.owner_details?.[0]?.user_id
    ? mapUserOptions(parentProjectDetails?.owner_details)
    : [];

  const parentCoOwnerDetails = parentProjectDetails?.co_owner_details?.[0]
    ?.user_id
    ? mapUserOptions(parentProjectDetails?.co_owner_details)
    : [];

  const parentEditUserDetails = parentProjectDetails?.edit_user_details?.[0]
    ?.user_id
    ? mapUserOptions(parentProjectDetails?.edit_user_details)
    : [];

  const parentViewUserDetails = parentProjectDetails?.view_user_details
    ?.user_id
    ? mapUserOptions(parentProjectDetails?.view_user_details)
    : [];

  const parentMilestoneDetails = mapProjectMilestonesToRows(
    parentProjectDetails?.milestones,
    milestoneNameOptions,
  );

  return {
    projectName: parentProjectDetails?.name || "",
    projectStatus: parentProjectDetails?.status || "",
    modelYear: parentModelYear || null,
    trademark: parentProjectDetails?.trade_mark || "",
    vehiclePlatform: parentProjectDetails?.vehicle_platform || "",
    country: parentCountryData?.country || "",
    timeZone: parentProjectDetails?.region || "",
    architecture: parentProjectDetails?.architecture_name || "",
    numSeats: parentProjectDetails?.no_of_seats || "",
    zoneNames: parentZoneNameDetails,
    vehicleClass: parentProjectDetails?.veh_class,
    vehicleType: parentProjectDetails?.vehicle_type || "",
    targetMarket: parentTargetMarket,
    deliveryMarket: parentDeliveryMarketDetails || [],
    vehicleFunction: parentVehicleFunctionDetails || [],
    powertrainClass: parentPowerTrainClassDetails || [],
    manufacturerDetails: parentMftDetails || [],
    owner: parentOwnerDetails || [],
    coOwners: parentCoOwnerDetails || [],
    editors: parentEditUserDetails || [],
    viewers: parentViewUserDetails || [],
    milestones: parentMilestoneDetails,
  };
};

const applyRecreateProjectState = (setters, state) => {
  setters.setProjectName(state.projectName);
  setters.setProjectStatus(state.projectStatus);
  setters.setModelYear(state.modelYear);
  setters.setTrademark(state.trademark);
  setters.setVehiclePlatform(state.vehiclePlatform);
  setters.setCountry(state.country);
  setters.setTimeZone(state.timeZone);
  setters.setArchitecture(state.architecture);
  setters.setNumSeats(state.numSeats);
  setters.setZoneNames(state.zoneNames);
  setters.setVehicleClass(state.vehicleClass);
  setters.setVehicleType(state.vehicleType);
  setters.setTargetMarket(state.targetMarket);
  setters.setDeliveryMarket(state.deliveryMarket);
  setters.setVehicleFunction(state.vehicleFunction);
  setters.setPowertrainClass(state.powertrainClass);
  setters.setManufacturerDetails(state.manufacturerDetails);
  setters.setOwner(state.owner);
  setters.setCoOwners(state.coOwners);
  setters.setEditors(state.editors);
  setters.setViewers(state.viewers);
  setters.setMilestoneRows(state.milestones);
  setters.setMilestoneDraft(createMilestoneRow());
};

let milestoneSequenceId = 0;
const generateMilestoneId = () => {
  milestoneSequenceId += 1;
  return `milestone-${Date.now()}-${milestoneSequenceId}`;
};

const createMilestoneRow = (overrides = {}) => ({
  id: generateMilestoneId(),
  name: "",
  milestoneDate: null,
  ...overrides,
});

const parseMilestoneDate = (milestoneDate) => {
  if (!milestoneDate) return null;
  const parsedDate = moment(milestoneDate, ["MM-YYYY", "YYYY-MM"], true);
  if (parsedDate.isValid()) return parsedDate.startOf("month");

  const fallbackDate = moment(milestoneDate);
  return fallbackDate.isValid() ? fallbackDate.startOf("month") : null;
};

const mapProjectMilestonesToRows = (
  milestones = [],
  milestoneNameOptions = [],
) => {
  if (!Array.isArray(milestones) || milestones.length === 0) {
    return [];
  }

  return milestones
    .map((milestone) => {
      const name = milestone?.name || "";
      const milestoneDate = parseMilestoneDate(milestone?.milestone_date);
      const selectedMilestoneOption = milestoneNameOptions.find(
        (option) => option.value === name,
      );

      return createMilestoneRow({
        name,
        milestoneDate,
        sequence: milestone?.sequence ?? selectedMilestoneOption?.sequence,
      });
    })
    .filter((milestone) => milestone.name && milestone.milestoneDate);
};

const getVehicleStatusList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

const milestoneDatePickerSx = {
  width: "160px !important",
  height: "250px !important",
  "& .MuiPickersLayout-root": {
    minWidth: "0 !important",
    maxWidth: "160px !important",
  },
  "& .MuiPickersLayout-contentWrapper": {
    padding: 0,
  },
  "& .MuiPickersCalendarHeader-switchViewButton": {
    display: "none",
  },
  "& .MuiPickersCalendarHeader-root": {
    position: "relative",
    paddingLeft: "1px",
    paddingRight: "1px",
    justifyContent: "space-between",
    minHeight: "30px",
    marginBottom: 0,
  },
  "& .MuiPickersCalendarHeader-labelContainer": {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  "& .MuiPickersCalendarHeader-label": {
    fontSize: "14px",
  },
  "& .MuiPickersArrowSwitcher-root": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  "& .MuiPickersArrowSwitcher-spacer": {
    display: "none",
  },
  "& .MuiPickersArrowSwitcher-button": {
    padding: 0,
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
};

const buildProjectRequestPayload = ({
  projectName,
  currentProjectID,
  trademark,
  vehiclePlatform,
  projectStatus,
  architecture,
  manufacturerDetails,
  zoneNames,
  vehicleClass,
  powertrainClass,
  vehicleFunction,
  country,
  timeZone,
  deliveryMarket,
  coOwners,
  editors,
  viewers,
  targetMarket,
  vehicleType,
  numSeats,
  modelYear,
  milestones,
  includeEditorsViewers = false,
}) => ({
  name: projectName?.trim(),
  ...(currentProjectID ? { parent_project_id: currentProjectID } : null),
  ...(trademark?.length > 0 ? { trade_mark: trademark?.trim() || "" } : null),
  vehicle_platform: vehiclePlatform?.trim() || "",
  status: projectStatus || "",
  architecture_name: architecture || "",
  mft_details: manufacturerDetails?.map((data) => data?.value),
  ...(zoneNames?.length > 0 && architecture?.toLowerCase() === "zonal"
    ? {
      zones: zoneNames?.map((data) => ({
        zone_name: data?.label,
        position: data?.value,
        sequence: data?.sequence,
      })),
    }
    : {}),
  vehicle_class: Number.parseInt(vehicleClass || "", 10),
  pwrtrn_class: powertrainClass?.map((str) => Number.parseInt(str?.value, 10)),
  ...(vehicleFunction?.length > 0
    ? { vehicle_funcs: vehicleFunction?.map((str) => Number.parseInt(str?.value, 10)) }
    : {}),
  current_time: moment().format(),
  country: country || "",
  region: timeZone || "",
  ...(deliveryMarket?.length > 0
    ? { delivery_market: deliveryMarket?.map((data) => data.value) }
    : {}),
  ...(coOwners?.length > 0
    ? { co_owners: coOwners?.map((data) => data?.value) }
    : null),
  ...(includeEditorsViewers && editors?.length > 0
    ? { edit_users: editors?.map((data) => data?.value) }
    : null),
  ...(includeEditorsViewers && viewers?.length > 0
    ? { view_users: viewers?.map((data) => data?.value) }
    : null),
  target_market: targetMarket || "",
  vehicle_type: vehicleType || "",
  no_of_seats: numSeats || "",
  model_year: modelYear?.$y,
  ...(milestones?.length > 0 ? { milestones } : null),
});

const buildFiltersParams = ({
  page_number,
  page_size,
  filtersPC,
  filterVehicleClass,
}) => {
  const isAllStatus = filtersPC?.ps_all;
  const isAllArchitecture = filtersPC?.at_all;

  return {
    page_number,
    page_size,
    order_by_sort: filtersPC?.sortOrder || "DESC",
    order_by_column: filtersPC?.sortBy || "created_at",
    status: isAllStatus ? undefined : filtersPC?.statusGroup?.join(","),
    vehicle_class:
      !filterVehicleClass || filterVehicleClass.length === 0
        ? undefined
        : filterVehicleClass.map((d) => d.value).join(","),
    is_zonal:
      isAllArchitecture ||
      filtersPC?.doubleGroup?.CENTRALIZED === filtersPC?.doubleGroup?.ZONAL
        ? undefined
        : filtersPC?.doubleGroup?.ZONAL,
  };
};

const atFilter = ["CENTRALIZED", "ZONAL"];
const singleColumnRows = [1, 3, 4, 5, 8, 9, 10, 11, 12, 13, 14, 15];

const singleValueMap = {
  "ACTIVE PRE SOP": 100,
  "ACTIVE POST SOP": 200,
  DRAFT: 101,
  DEACTIVATED: 300,
  COMPLETED: 500,
};
const ALL_STATUS_VALUES = [100, 101, 200, 300, 500];
const projectStatusConfig = {
  100: { label: "Active Pre SOP", color: "black", showInfo: true },
  101: { label: "Draft", color: "black", showInfo: true },
  200: { label: "Active Post SOP", color: "black", showInfo: true },
  300: { label: "Deactivated", color: "red", showInfo: false },
  500: { label: "Completed", color: "green", showInfo: true },
};

const getProjectStatusColor = (statusCode) => {
  switch (statusCode) {
    case 101:
      return "#007bff";
    case 100:
    case 200:
      return "#2ecc71";
    case 300:
      return "#808080";
    case 500:
      return "#000000";
    default:
      return "#000";
  }
};

const formatLabel = (str) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) =>
      word === "sop" ? "SOP" : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

const getProjectStatusView = (statusCode) => {
  const status = projectStatusConfig[statusCode];
  if (!status) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          textAlign: "end",
          color: getProjectStatusColor(statusCode),
        }}
      >
        {status.label}
      </Typography>
      <Box>
        {status.showInfo && (
          <InfoButton infoKey={"project_status"} color={"black"} />
        )}
      </Box>
    </Box>
  );
};

const MilestoneSection = ({
  rows,
  draft,
  onAddMilestone,
  onRemoveMilestone,
  milestoneNameOptions = [],
}) => {
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [datePickerView, setDatePickerView] = useState("year");
  const [pendingMilestoneDate, setPendingMilestoneDate] = useState(null);

  const [activeMilestoneName, setActiveMilestoneName] = useState("");

  const milestoneDates = draft?.milestoneDates || {};

  const handleOpenDatePicker = (event, milestoneName) => {
    setDatePickerAnchor(event.currentTarget);
    setActiveMilestoneName(milestoneName);
    setPendingMilestoneDate(milestoneDates[milestoneName] || null);
    setDatePickerView("year");
  };

  const handleCloseDatePicker = () => {
    setDatePickerAnchor(null);
    setActiveMilestoneName("");
    setPendingMilestoneDate(null);
    setDatePickerView("year");
  };

  const updateMilestoneDate = (date) => {
    if (!activeMilestoneName) return;

    const selectedMilestoneOption = milestoneNameOptions.find(
      (option) => option.value === activeMilestoneName,
    );

    onAddMilestone([
      createMilestoneRow({
        name: activeMilestoneName,
        milestoneDate: date,
        sequence: selectedMilestoneOption?.sequence,
      }),
    ]);
  };

  const handleSelectedPickerClick = (event) => {
    const selectedButton = event.target.closest("button.Mui-selected");
    if (!selectedButton) return;

    const selectedText = selectedButton.textContent?.trim();
    const baseDate =
      pendingMilestoneDate || milestoneDates[activeMilestoneName] || moment();

    if (datePickerView === "year" && /^\d{4}$/.test(selectedText)) {
      setPendingMilestoneDate(
        baseDate.clone().year(Number(selectedText)).startOf("month"),
      );
      setDatePickerView("month");
      return;
    }

    if (datePickerView === "month") {
      const monthIndex = [...moment.monthsShort(), ...moment.months()]
        .findIndex(
          (month) => month.toLowerCase() === selectedText?.toLowerCase(),
        ) % 12;

      if (monthIndex === -1) return;

      updateMilestoneDate(baseDate.clone().month(monthIndex).startOf("month"));
      handleCloseDatePicker();
    }
  };

  const addedMilestoneNames = new Set(
    rows.map((milestone) => milestone.name?.trim()).filter(Boolean),
  );
  const availableMilestoneNameOptions = milestoneNameOptions.filter(
    (option) => !addedMilestoneNames.has(option.value),
  );
  const milestoneDropdownOptions =
    availableMilestoneNameOptions.length > 0
      ? availableMilestoneNameOptions
      : [{ label: "No data", value: "__no_data__" }];
  const getMilestoneSequence = (name) =>
    milestoneNameOptions.find((option) => option.value === name)?.sequence ??
    Number.MAX_SAFE_INTEGER;
  const sortedRows = rows
    .slice()
    .sort(
      (a, b) =>
        (a.sequence ?? getMilestoneSequence(a.name)) -
          (b.sequence ?? getMilestoneSequence(b.name)) ||
        a.name.localeCompare(b.name),
    );

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Box
        sx={{
          mb: 2,
          overflowY: "auto",
          border: "2px solid #d9d9d9",
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1, p: 1 }}>Milestones</Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "end",
            p: 1,
          }}
        >
          {milestoneDropdownOptions.map((option) => (
            <React.Fragment key={option.value}>
              <CustomFormTextFieldV3
                label="Milestone Name"
                required
                value={option.value === "__no_data__" ? "" : option.value}
                placeholder="No data"
                disabled
              />
              <Box>
                <Typography
                  sx={{
                    mb: 0.5,
                    color: "#6f7174",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                  }}
                >
                  Milestone Date <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="MM-YYYY"
                  value={milestoneDates[option.value]?.format("MM-YYYY") || ""}
                  onClick={(event) => {
                    if (option.value === "__no_data__") return;
                    handleOpenDatePicker(event, option.value);
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <CalendarMonthIcon
                        sx={{
                          fontSize: 18,
                          color: "#000",
                          cursor: "pointer",
                        }}
                      />
                    ),
                  }}
                  sx={{
                    cursor:
                      option.value === "__no_data__" ? "not-allowed" : "pointer",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      "& fieldset": {
                        borderColor: "#d9d9d9",
                        borderRadius: "0px",
                      },
                      "&:hover fieldset": { borderColor: "#8c8c8c" },
                      "&.Mui-focused fieldset": { borderColor: "#000" },
                    },
                  }}
                />
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {rows.length > 0 ? (
        <Stack
          direction="row"
          mt={2}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            maxHeight: 72,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            pb: 0.5,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#bfbfbf",
              borderRadius: 10,
            },
          }}
        >
          {sortedRows.map((milestone) => (
            <Chip
              key={milestone.id}
              label={`${milestone.name} - ${milestone.milestoneDate?.format("MM-YYYY")}`}
              onDelete={() => onRemoveMilestone(milestone.id)}
              sx={{
                backgroundColor: "#000",
                color: "white",
                borderRadius: 0,
                px: 1.2,
                py: 0.5,
                mr: 1.2,
                "& .MuiChip-deleteIcon": {
                  color: "white",
                  fontSize: 18,
                  "&:hover": {
                    color: "white",
                  },
                },
              }}
            />
          ))}
        </Stack>
      ) : null}

      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleCloseDatePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { overflow: "visible", borderRadius: "0px" } } }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box onClickCapture={handleSelectedPickerClick}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={
                pendingMilestoneDate ||
                milestoneDates[activeMilestoneName] ||
                null
              }
              onChange={(newValue) => {
                if (!newValue) return;
                if (datePickerView === "year") {
                  setPendingMilestoneDate(newValue.startOf("month"));
                  setDatePickerView("month");
                  return;
                }
                updateMilestoneDate(newValue.startOf("month"));
                handleCloseDatePicker();
              }}
              views={["year", "month"]}
              view={datePickerView}
              slotProps={{ actionBar: { actions: [] } }}
              sx={milestoneDatePickerSx}
            />
          </Box>
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};

MilestoneSection.propTypes = {
  rows: PropTypes.array.isRequired,
  draft: PropTypes.object,
  onAddMilestone: PropTypes.func.isRequired,
  onRemoveMilestone: PropTypes.func.isRequired,
  milestoneNameOptions: PropTypes.array,
};

const NoProjects = () => (
  <Typography
    sx={{
      textAlign: "center",
      mt: 3,
      fontSize: 16,
      fontWeight: 500,
      color: "#7d7d7d",
    }}
  >
    No projects found
  </Typography>
);

const ProjectGridView = ({
  isDense,
  projects,
  onRecreate,
  onViewMore,
}) => {
  if (!Array.isArray(projects) || projects.length === 0) {
    return (
      <Box
        sx={
          isDense
            ? { gridColumn: "1 / -1" }
            : { display: "flex", justifyContent: "center", width: "100%" }
        }
      >
        <NoProjects />
      </Box>
    );
  }

  const ViewComponent = isDense ? DenseViewBox : CompactViewBox;

  return (
    <>
      {projects.map((item, index) => (
        <ViewComponent
          key={item.project_id || index}
          onRecreateClick={() => onRecreate(item.project_id)}
          architectureName={item?.architecture_name}
          imageUrl={item || {}}
          title={item?.name}
          vehicleClass={`${
            item?.vehicle_class_details?.[0]?.name || ""
          } - ${item?.vehicle_class_details?.[0]?.class || ""} - ${
            item?.vehicle_class_details?.[0]?.sub_class || ""
          }`}
          projectOwner={item?.owned_by?.name}
          trademark={item?.trade_mark}
          vehiclePlatform={item?.vehicle_platform}
          createdAt={dayjs(item?.created_at).format("DD MMM YYYY, hh:mm A")}
          updatedAt={dayjs(item?.updated_at).format("DD MMM YYYY, hh:mm A")}
          status={getProjectStatusView(item.status)}
          archive_status={item?.archive_status}
          onViewMore={() => onViewMore(item)}
        />
      ))}
    </>
  );
};

ProjectGridView.propTypes = {
  isDense: PropTypes.bool.isRequired,
  projects: PropTypes.array.isRequired,
  onRecreate: PropTypes.func.isRequired,
  onViewMore: PropTypes.func.isRequired,
};

const componentMap = {
  CustomFormTextFieldV3,
  CustomSelectV2,
  CustomSelectV3,
  CustomMultiSelectV2,
  CustomDatePicker,
  CustomDatePickerV2,
  CustomRadioButtonV3,
  MilestoneSection,
};

const getNextStatusFilter = (prev, item, state) => {
  if (item === "ALL") {
    return {
      ...prev,
      ps_all: state,
      statusGroup: state ? ALL_STATUS_VALUES : [100],
    };
  }

  const value = singleValueMap[item];
  let updatedStatus = [...prev.statusGroup];

  if (state) {
    if (!updatedStatus.includes(value)) {
      updatedStatus.push(value);
    }
  } else {
    updatedStatus = updatedStatus.filter((v) => v !== value);
  }

  if (updatedStatus.length === 0) {
    return {
      ...prev,
      ps_all: state,
      statusGroup: state ? ALL_STATUS_VALUES : [100],
    };
  }

  const isAllSelected = updatedStatus.length === ALL_STATUS_VALUES.length;

  return {
    ...prev,
    ps_all: isAllSelected,
    statusGroup: updatedStatus,
  };
};

const getNextArchitectureDoubleGroup = (doubleGroup, item, state) => {
  if (item === "ALL") {
    return {
      CENTRALIZED: state,
      ZONAL: state,
    };
  }
  return {
    CENTRALIZED: item === "CENTRALIZED" ? state : doubleGroup.CENTRALIZED,
    ZONAL: item === "ZONAL" ? state : doubleGroup.ZONAL,
  };
};

const getInitialFiltersPC = () => ({
  at_all: true,
  ps_all: true,
  doubleGroup: { CENTRALIZED: true, ZONAL: true },
  singleGroup: null,
  statusGroup: [100, 101, 200, 300, 500],
  sortBy: "created_at",
  sortOrder: "DESC",
});

const getDiscardedFiltersPC = () => ({
  at_all: true,
  ps_all: true,
  doubleGroup: { CENTRALIZED: true, ZONAL: true },
  statusGroup: ALL_STATUS_VALUES,
  sortBy: "created_at",
  sortOrder: "ASC",
});

const isArchitectureChecked = (item, filtersPC) =>
  (item === "ALL" && filtersPC.at_all) ||
  (item === "CENTRALIZED" && filtersPC.doubleGroup.CENTRALIZED) ||
  (item === "ZONAL" && filtersPC.doubleGroup.ZONAL);

const isStatusChecked = (item, filtersPC) =>
  (item === "ALL" && filtersPC.ps_all) ||
  filtersPC.statusGroup.includes(singleValueMap[item]);

const getVehicleClassFilterOptions = (vehicleClassData) =>
  Array.isArray(vehicleClassData) && vehicleClassData.length > 0
    ? vehicleClassData.map((item) => ({
        name: item.name,
        class: item.class,
        sub_class: item.sub_class,
        label: `${item.name} (${item.class} - ${item.sub_class})`,
        value: item.id,
      }))
    : [];

const getMilestoneSequence = (milestoneNameOptions, name) =>
  milestoneNameOptions.find((option) => option.value === name)?.sequence ??
  Number.MAX_SAFE_INTEGER;

const buildMilestonesPayloadFromRows = (milestoneRows, milestoneNameOptions) =>
  milestoneRows
    .slice()
    .sort(
      (a, b) =>
        (a.sequence ?? getMilestoneSequence(milestoneNameOptions, a.name)) -
          (b.sequence ?? getMilestoneSequence(milestoneNameOptions, b.name)) ||
        a.name.localeCompare(b.name),
    )
    .map((milestone) => ({
      name: milestone.name?.trim(),
      milestone_date: milestone.milestoneDate?.format("MM-YYYY"),
      status: true,
    }))
    .filter((milestone) => milestone.name && milestone.milestone_date);

const addBatchMilestones = (prev, milestonesToAdd) => {
  if (milestonesToAdd.some((m) => !m.name || !m.milestoneDate)) {
    return prev;
  }
  const existingNames = new Set(prev.map((row) => row.name));
  return [
    ...prev,
    ...milestonesToAdd.filter((m) => !existingNames.has(m.name)),
  ];
};

const addSingleMilestoneFromDraft = (prev, draft, milestoneNameOptions) => {
  const trimmedName = draft.name?.trim();
  if (
    !trimmedName ||
    trimmedName.length < 3 ||
    !draft.milestoneDate ||
    prev.some((m) => m.name === trimmedName)
  ) {
    return prev;
  }

  const selectedOption = milestoneNameOptions.find(
    (option) => option.value === trimmedName,
  );

  return [
    ...prev,
    createMilestoneRow({
      name: trimmedName,
      milestoneDate: draft.milestoneDate,
      sequence: selectedOption?.sequence,
    }),
  ];
};

const hasMinLength = (str, min = 3) => (str?.trim()?.length ?? 0) >= min;
const hasNonEmptyArray = (arr) => (arr?.length ?? 0) > 0;
const hasValidTextInputs = (values) => values.every((val) => hasMinLength(val));
const hasRequiredFields = (...fields) => fields.every(Boolean);
const checkMilestonesComplete = (milestoneRows, milestoneNameOptions) =>
  milestoneRows?.length === milestoneNameOptions?.length;

const checkIsAddDisabled = ({
  projectName,
  projectStatus,
  modelYear,
  architecture,
  vehicleClass,
  vehicleType,
  country,
  timeZone,
  vehiclePlatform,
  targetMarket,
  numSeats,
  manufacturerDetails,
  powertrainClass,
  milestoneRows,
  milestoneNameOptions,
}) => {
  const isTextValid = hasValidTextInputs([
    projectName,
    architecture,
    country,
    timeZone,
    vehiclePlatform,
    targetMarket,
  ]);
  if (!isTextValid) return true;

  const isRequiredValid = hasRequiredFields(
    projectStatus,
    modelYear,
    vehicleClass,
    vehicleType,
    numSeats,
  );
  if (!isRequiredValid) return true;

  const isArraysValid =
    hasNonEmptyArray(manufacturerDetails) && hasNonEmptyArray(powertrainClass);
  if (!isArraysValid) return true;

  return !checkMilestonesComplete(milestoneRows, milestoneNameOptions);
};

const resetProjectFormState = (setters) => {
  setters.setProjectName("");
  setters.setCurrentProjectID(null);
  setters.setProjectStatus("");
  setters.setModelYear(null);
  setters.setTrademark("");
  setters.setVehiclePlatform("");
  setters.setCountry("");
  setters.setTimeZone("");
  setters.setArchitecture("");
  setters.setNumSeats("");
  setters.setZoneNames([]);
  setters.setVehicleClass("");
  setters.setVehicleType("");
  setters.setTargetMarket("");
  setters.setDeliveryMarket([]);
  setters.setVehicleFunction([]);
  setters.setPowertrainClass([]);
  setters.setManufacturerDetails([]);
  setters.setCoOwners([]);
  setters.setViewers([]);
  setters.setEditors([]);
  setters.setMilestoneRows([]);
  setters.setMilestoneDraft(createMilestoneRow());
};

const isValidObject = (data) =>
  typeof data === "object" && !Array.isArray(data);

const hasValidValues = (obj) =>
  Object.values(obj).some((pr) => pr !== "" && pr?.length !== 0);

const safeJsonParse = (str, fallback = []) => {
  if (!str) return fallback;
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

const mapUserIdsFromJson = (jsonStr) =>
  safeJsonParse(jsonStr, []).map((u) => u?.user_id);

const mapIdsFromJson = (jsonStr) =>
  safeJsonParse(jsonStr, []).map((item) => item?.id);

const parseExcelProjectRow = (pr) => ({
  name: pr?.name || "",
  trade_mark: pr?.trade_mark || "",
  vehicle_platform: pr?.vehicle_platform || "",
  status: pr?.status || "",
  architecture_name: pr?.architecture_name || "",
  mft_details: safeJsonParse(pr?.manufacture_details, []),
  ...(pr?.zones ? { zones: safeJsonParse(pr.zones, null) } : {}),
  vehicle_class: safeJsonParse(pr?.vehicle_class_details, null)?.id || "",
  pwrtrn_class: mapIdsFromJson(pr?.powertrain_class_details),
  vehicle_funcs: mapIdsFromJson(pr?.vehicle_function_details),
  country: pr?.country || "",
  region: pr?.region || "",
  delivery_market: pr?.delivery_market
    ? pr.delivery_market.split(",").map((i) => i.trim())
    : [],
  target_market: pr?.target_market || "",
  vehicle_type: pr?.vehicle_type || "",
  no_of_seats: pr?.no_of_seats || "",
  model_year: pr?.model_year || "",
  co_owners: mapUserIdsFromJson(pr?.co_owner_details),
  edit_users: mapUserIdsFromJson(pr?.edit_user_details),
  view_users: mapUserIdsFromJson(pr?.view_user_details),
});



const buildRecreateExtraFields = ({
  usersListData,
  editors,
  setEditors,
  viewers,
  setViewers,
}) => [
  {
    component: "CustomMultiSelectV2",
    props: {
      label: "Editor",
      required: false,
      selectedValues: editors,
      setSelectedValues: setEditors,
      optionsList:
        Array.isArray(usersListData) && usersListData.length > 0
          ? usersListData.map((item) => ({
              label: item.fullname,
              subLabel: item.email,
              value: item?.user_id,
            }))
          : [],
      renderOption: (props, option) => (
        <li {...props} key={option.value}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              {option.subLabel}
            </Typography>
          </Box>
        </li>
      ),
      getOptionLabel: (option) => option.label,
    },
  },
  {
    component: "CustomMultiSelectV2",
    props: {
      label: "Viewer",
      required: false,
      selectedValues: viewers,
      setSelectedValues: setViewers,
      optionsList:
        Array.isArray(usersListData) && usersListData.length > 0
          ? usersListData.map((item) => ({
              label: item.fullname,
              subLabel: item.email,
              value: item?.user_id,
            }))
          : [],
      renderOption: (props, option) => (
        <li {...props} key={option.value}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              {option.subLabel}
            </Typography>
          </Box>
        </li>
      ),
      getOptionLabel: (option) => option.label,
    },
  },
];

const getProjectStatusOptions = (metaData) =>
  Object.entries(metaData?.project_status || {})
    .slice(0, 3)
    .map(([key, value]) => ({
      label: formatLabel(key.replaceAll("_", " ")),
      value,
    }));

const getCountryOptions = (countryData) =>
  (countryData || []).map((item) => ({
    label: item?.country,
    value: item?.country,
  }));

const getTimeZoneOptions = (countryData) =>
  (countryData || []).map((item) => ({
    label: item?.time_zone,
    value: item?.time_zone,
  }));

const getArchitectureOptions = (adminDefaultData) =>
  (adminDefaultData?.data?.architecture_types || []).map((item) => ({
    label: item.name,
    value: item.name,
  }));

const getZoneOptions = (adminDefaultData) =>
  (adminDefaultData?.data?.zone_details || []).map((item) => ({
    label: item?.position_name,
    value: item?.zone_position,
    sequence: item?.sequence,
  }));

const sortVehicleClasses = (a, b) => {
  if (a.class !== b.class) return a.class < b.class ? -1 : 1;
  if (a.sub_class !== b.sub_class) return a.sub_class < b.sub_class ? -1 : 1;
  return 0;
};

const getVehicleClassOptions = (image) =>
  (image || [])
    .map((item) => ({
      name: item?.name,
      class: item?.class,
      sub_class: item?.sub_class,
      label: `${item?.name} (${item?.class} - ${item?.sub_class})`,
      value: item?.id,
      image: item?.imageUrl,
    }))
    .sort(sortVehicleClasses);

const getVehicleTypeOptions = (metaData) =>
  (metaData?.vehicle_type || []).map((data) => ({
    value: data?.key,
    label: data?.value,
  }));

const getDeliveryMarketOptions = (adminDefaultData) =>
  (adminDefaultData?.data?.product_delivery_markets || []).map((item) => ({
    label: item,
    value: item,
  }));

const getVehicleFunctionOptions = (vehicleFunctionData) =>
  (vehicleFunctionData || []).map((item) => ({
    label: `${item?.name} (${item?.abbreviation})`,
    value: item?.id,
  }));

const getPowertrainClassOptions = (powerTrainClassData) =>
  (powerTrainClassData || []).map((item) => ({
    label: `${item?.name} (${item?.abbreviation})`,
    value: item?.id,
  }));

const getManufacturerOptions = (adminDefaultData) =>
  (adminDefaultData?.data?.vehicle_manufacturers || []).map((item) => ({
    label: item?.name,
    subLabel: item?.address,
    value: item,
  }));

const getUserOptions = (usersListData) =>
  (usersListData || []).map((item) => ({
    label: item.fullname,
    subLabel: item.email,
    value: item?.user_id,
  }));

const handleNumSeatsChange = (val, setNumSeats) => {
  const num = Number(val);
  if (num >= 1 && num <= 10) {
    setNumSeats(num);
  } else if (val === "") {
    setNumSeats("");
  }
};

const handleMilestonesAdd = (
  milestonesToAdd,
  milestoneDraft,
  milestoneNameOptions,
  setMilestoneRows,
  setMilestoneDraft,
) => {
  if (Array.isArray(milestonesToAdd) && milestonesToAdd.length > 0) {
    setMilestoneRows((prev) => addBatchMilestones(prev, milestonesToAdd));
  } else {
    setMilestoneRows((prev) =>
      addSingleMilestoneFromDraft(prev, milestoneDraft, milestoneNameOptions),
    );
  }
  setMilestoneDraft(createMilestoneRow());
};

const buildProjectFormLayout = ({
  projectName,
  setProjectName,
  projectStatus,
  setProjectStatus,
  metaData,
  modelYear,
  setModelYear,
  milestoneRows,
  setMilestoneRows,
  milestoneDraft,
  setMilestoneDraft,
  milestoneNameOptions,
  trademark,
  setTrademark,
  vehiclePlatform,
  setVehiclePlatform,
  country,
  setCountry,
  countryData,
  timeZone,
  setTimeZone,
  adminDefaultData,
  architecture,
  setArchitecture,
  numSeats,
  setNumSeats,
  zoneNames,
  setZoneNames,
  image,
  vehicleClass,
  setVehicleClass,
  vehicleType,
  setVehicleType,
  targetMarket,
  setTargetMarket,
  deliveryMarket,
  setDeliveryMarket,
  vehicleFunction,
  setVehicleFunction,
  vehicleFunctionData,
  powertrainClass,
  setPowertrainClass,
  powerTrainClassData,
  manufacturerDetails,
  setManufacturerDetails,
  coOwners,
  setCoOwners,
  usersListData,
  openRecreateProject,
  editors,
  setEditors,
  viewers,
  setViewers,
}) => [
  {
    component: "CustomFormTextFieldV3",
    props: {
      label: "Project Name",
      required: true,
      type: "text",
      fldType: "text",
      value: projectName,
      setValue: setProjectName,
      placeholder: "Enter Project Name",
      infoname: "name_of_the_project",
    },
  },
  {
    component: "CustomSelectV3",
    props: {
      size: "small",
      infoname: "project_status",
      placeholder: "Select Project Status",
      label: "Project Status",
      required: true,
      handleOnChange: (e) => setProjectStatus(e.target.value),
      selectedValue: projectStatus,
      optionsList: getProjectStatusOptions(metaData),
    },
  },
  {
    component: "CustomDatePickerV2",
    props: {
      label: "Select a Model Year",
      required: true,
      value: modelYear,
      onChange: (value) => {
        console.log("---model year---", value);
        setModelYear(value);
      },
    },
  },
  {
    component: "MilestoneSection",
    props: {
      rows: milestoneRows,
      draft: milestoneDraft,
      milestoneNameOptions,
      onAddMilestone: (milestonesToAdd = []) =>
        handleMilestonesAdd(
          milestonesToAdd,
          milestoneDraft,
          milestoneNameOptions,
          setMilestoneRows,
          setMilestoneDraft,
        ),
      onRemoveMilestone: (id) =>
        setMilestoneRows((prev) => prev.filter((row) => row.id !== id)),
    },
  },
  {
    component: "CustomFormTextFieldV3",
    props: {
      label: "Trademark",
      required: false,
      value: trademark,
      setValue: setTrademark,
      placeholder: "Enter Trademark",
      infoname: "trademark",
    },
  },
  {
    component: "CustomFormTextFieldV3",
    props: {
      label: "Vehicle Platform",
      required: true,
      value: vehiclePlatform,
      setValue: setVehiclePlatform,
      placeholder: "Enter Vehicle Platform",
      infoname: "vehicle_platform",
    },
  },
  {
    component: "CustomSelectV3",
    props: {
      placeholder: "Select Country",
      label: "Country",
      required: true,
      handleOnChange: (e) => {
        console.log("select country", e.target.value);
        setCountry(e.target.value);
      },
      selectedValue: country,
      optionsList: getCountryOptions(countryData),
    },
  },
  {
    component: "CustomSelectV3",
    props: {
      placeholder: "Select Time Zone",
      label: "Time Zone",
      required: true,
      handleOnChange: (e) => setTimeZone(e.target.value),
      selectedValue: timeZone,
      optionsList: getTimeZoneOptions(countryData),
    },
  },
  {
    component: "CustomRadioButtonV3",
    props: {
      label: "Type of Architecture",
      required: true,
      infoname: "type_of_architecture",
      data: getArchitectureOptions(adminDefaultData),
      value: architecture,
      onChange: (e) => setArchitecture(e.target.value),
      direction: "column",
      gap: 1.5,
    },
  },
  {
    component: "CustomFormTextFieldV3",
    props: {
      label: "Number of Seats",
      fldType: "number",
      required: true,
      value: numSeats,
      setValue: (val) => handleNumSeatsChange(val, setNumSeats),
      placeholder: "Enter Number of Seats",
      type: "number",
      inputProps: {
        min: 1,
        max: 10,
      },
    },
  },
  {
    component: "CustomMultiSelectV2",
    props: {
      is_zonal: architecture === "ZONAL",
      label: "Zone Name",
      required: true,
      selectedValues: zoneNames,
      setSelectedValues: setZoneNames,
      optionsList: getZoneOptions(adminDefaultData),
    },
  },
  {
    component: "CustomSelectV3",
    props: {
      placeholder: "Select Vehicle Class",
      label: "Vehicle Class",
      infoname: "vehicle_class",
      required: true,
      handleOnChange: (e) => {
        console.log("Select Vehicle Class", e.target.value);
        setVehicleClass(e.target.value);
      },
      selectedValue: vehicleClass,
      optionsList: getVehicleClassOptions(image),
    },
  },
  {
    component: "CustomSelectV3",
    props: {
      placeholder: "Select Vehicle Type",
      label: "Vehicle Type",
      required: true,
      handleOnChange: (e) => setVehicleType(e.target.value),
      selectedValue: vehicleType,
      optionsList: getVehicleTypeOptions(metaData),
    },
  },
  {
    component: "CustomSelectV3",
    props: {
      placeholder: "Select Target Market",
      label: "Target Market",
      infoname: "target_market",
      required: true,
      handleOnChange: (e) => setTargetMarket(e.target.value),
      selectedValue: targetMarket,
      optionsList: getDeliveryMarketOptions(adminDefaultData),
    },
  },
  {
    component: "CustomMultiSelectV2",
    props: {
      placeholder: "Select Delivery Market",
      label: "Delivery Market",
      infoname: "delivery_market",
      required: false,
      selectedValues: deliveryMarket,
      setSelectedValues: setDeliveryMarket,
      optionsList: getDeliveryMarketOptions(adminDefaultData),
    },
  },
  {
    component: "CustomMultiSelectV2",
    props: {
      label: "Vehicle Function",
      required: true,
      infoname: "vehicle_function",
      selectedValues: vehicleFunction,
      setSelectedValues: setVehicleFunction,
      optionsList: getVehicleFunctionOptions(vehicleFunctionData),
    },
  },
  {
    component: "CustomMultiSelectV2",
    props: {
      label: "Powertrain Class",
      required: true,
      selectedValues: powertrainClass,
      infoname: "power_train_class",
      setSelectedValues: setPowertrainClass,
      optionsList: getPowertrainClassOptions(powerTrainClassData),
    },
  },
  {
    component: "CustomMultiSelectV2",
    props: {
      label: "Manufacturer Details",
      required: true,
      selectedValues: manufacturerDetails,
      infoname: "manufacturer_name",
      setSelectedValues: setManufacturerDetails,
      optionsList: getManufacturerOptions(adminDefaultData),
      renderOption: (props, option) => (
        <li {...props} key={option.value}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              {option.subLabel}
            </Typography>
          </Box>
        </li>
      ),
      getOptionLabel: (option) => option.label,
    },
  },
  {
    component: "CustomMultiSelectV2",
    props: {
      label: "Co Owners",
      required: false,
      selectedValues: coOwners,
      setSelectedValues: setCoOwners,
      optionsList: getUserOptions(usersListData),
      renderOption: (props, option) => (
        <li {...props} key={option.value}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              {option.subLabel}
            </Typography>
          </Box>
        </li>
      ),
      getOptionLabel: (option) => option.label,
    },
  },
  ...(openRecreateProject
    ? buildRecreateExtraFields({
        usersListData,
        editors,
        setEditors,
        viewers,
        setViewers,
      })
    : []),
];

const fetchVehicleClassImages = async (vehicleClassData) =>
  Promise.all(
    vehicleClassData.map(async (item) => {
      try {
        const response = await GetSingleImages({
          image: item?.image,
          ignore_app_loading_spinner: true,
        });
        return { ...item, imageUrl: response };
      } catch {
        // Return empty string as fallback when image retrieval fails
        return { ...item, imageUrl: "" };
      }
    }),
  );

const getTimeZoneForCountry = (country, countryData) => {
  if (!country) return "";
  const selected = countryData?.find(
    (item) => item?.country?.toUpperCase() === country?.toUpperCase(),
  );
  return selected?.time_zone || "";
};

const navigateToProjectConfig = (navigate, value, item, contextData) => {
  if (value && item) {
    navigate(`/dashboard/project-config?project_id=${item.project_id}`, {
      state: {
        projectId: item?.project_id,
        adminDefaultData: contextData.adminDefaultData,
        powerTrainClassData: contextData.powerTrainClassData,
        vehicleFunctionData: contextData.vehicleFunctionData,
        metaData: contextData.metaData,
      },
    });
    return;
  }
  navigate(`/dashboard/project-config`);
};

const closeOrOpenDialog = (setOpen, value, onReset) => {
  setOpen(value);
  if (!value) {
    onReset();
  }
};

const resetDraftIfDuplicate = (
  milestoneDraft,
  milestoneRows,
  setMilestoneDraft,
) => {
  if (
    milestoneDraft.name &&
    milestoneRows.some((milestone) => milestone.name === milestoneDraft.name)
  ) {
    setMilestoneDraft((prev) => ({
      ...prev,
      name: "",
    }));
  }
};

const cleanModelYear = (rawModelYear) => {
  const modelYearVal = typeof rawModelYear === "string" ? rawModelYear.replaceAll(/\\?"/g, "").trim() : rawModelYear;
  if (!modelYearVal) return "";
  return Number(modelYearVal) || modelYearVal;
};

const extractMftDetails = (item) => {
  if (Array.isArray(item?.mft_details)) return item.mft_details;
  if (Array.isArray(item?.manufacture_details)) return item.manufacture_details;
  return [];
};

const extractVehicleClass = (item) => {
  if (item?.vehicle_class !== undefined && item?.vehicle_class !== null) {
    return item.vehicle_class;
  }
  return item?.vehicle_class_details?.id || item?.vehicle_class_details?.[0]?.id || null;
};

const extractIdList = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((entry) => entry?.id || entry?.value || entry).filter(Boolean);
};

const extractUserIds = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((user) => user?.user_id || user?.id).filter(Boolean);
};

const processJsonFile = (data) => {
  const item = Array.isArray(data) ? data[0] : data;
  if (!item) throw new Error("Invalid or empty JSON data.");

  const pwrtrnClass = Array.isArray(item.pwrtrn_class)
    ? item.pwrtrn_class
    : extractIdList(item.powertrain_class_details);

  const vehicleFuncs = Array.isArray(item.vehicle_funcs)
    ? item.vehicle_funcs
    : extractIdList(item.vehicle_function_details);

  const coOwners = Array.isArray(item.co_owners)
    ? item.co_owners
    : extractUserIds(item.co_owner_details);

  const zones = item.zones || item.project_zones;

  return {
    name: item.name || "",
    trade_mark: item.trade_mark || "",
    vehicle_platform: item.vehicle_platform || "",
    status: item.status || "",
    architecture_name: item.architecture_name || "",
    mft_details: extractMftDetails(item),
    vehicle_class: extractVehicleClass(item),
    pwrtrn_class: pwrtrnClass,
    vehicle_funcs: vehicleFuncs,
    current_time: item.current_time || moment().format(),
    country: item.country || "",
    region: item.region || "",
    delivery_market: Array.isArray(item.delivery_market) ? item.delivery_market : [],
    co_owners: coOwners,
    target_market: item.target_market || "",
    vehicle_type: item.vehicle_type || "",
    no_of_seats: item.no_of_seats || "",
    model_year: cleanModelYear(item.model_year),
    ...(zones ? { zones } : {}),
    milestones: Array.isArray(item.milestones) ? item.milestones : [],
  };
};

const processExcelFile = (excelData) => {
  const sheetNames = Object.keys(excelData || {});
  if (sheetNames.length === 0) throw new Error("Excel file has no sheets.");

  const firstSheet = excelData[sheetNames[0]];
  if (!Array.isArray(firstSheet) || firstSheet.length === 0) {
    throw new Error("First sheet of Excel file is empty.");
  }

  const row = firstSheet[0];
  const parseJsonField = (val, fallback) => {
    if (typeof val === "string") {
      try {
        return JSON.parse(val);
      } catch {
        return fallback;
      }
    }
    return val || fallback;
  };

  const zonesParsed = parseJsonField(row.zones || row.project_zones, null);

  return {
    name: row.name || row.Name || "",
    trade_mark: row.trade_mark || row["Trade Mark"] || "",
    vehicle_platform: row.vehicle_platform || row["Vehicle Platform"] || "",
    status: Number(row.status ?? row.Status) || row.status || "",
    architecture_name: row.architecture_name || row["Architecture Name"] || "",
    mft_details: parseJsonField(row.mft_details || row.manufacture_details, []),
    vehicle_class: Number(row.vehicle_class ?? row["Vehicle Class"]) || row.vehicle_class || null,
    pwrtrn_class: parseJsonField(row.pwrtrn_class, []),
    vehicle_funcs: parseJsonField(row.vehicle_funcs, []),
    current_time: row.current_time || moment().format(),
    country: row.country || row.Country || "",
    region: row.region || row.Region || "",
    delivery_market: parseJsonField(row.delivery_market, []),
    co_owners: parseJsonField(row.co_owners, []),
    target_market: row.target_market || row["Target Market"] || "",
    vehicle_type: Number(row.vehicle_type ?? row["Vehicle Type"]) || row.vehicle_type || "",
    no_of_seats: Number(row.no_of_seats ?? row["No of Seats"]) || row.no_of_seats || "",
    model_year: cleanModelYear(row.model_year ?? row["Model Year"]),
    ...(zonesParsed ? { zones: zonesParsed } : {}),
    milestones: parseJsonField(row.milestones, []),
  };
};

const parseImportedFileData = (fileType, importedFileData) => {
  if (fileType === "json") {
    return processJsonFile(importedFileData);
  }
  if (fileType === "excel") {
    return processExcelFile(importedFileData);
  }
  throw new Error("Unsupported file type.");
};

const handleImportedFile = ({
  fileType,
  importedFileData,
  dispatch,
  onSuccess,
}) => {
  if (!importedFileData) {
    showToastMessage({
      type: "error",
      message: "No data found in the imported file.",
    });
    return;
  }

  try {
    const importedProjectDetails = parseImportedFileData(
      fileType,
      importedFileData,
    );
    dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_project,
        params: {
          ...importedProjectDetails,
          current_time: moment().format(),
        },
        show_toast: true,
      }),
    ).then(onSuccess);
  } catch (error) {
    showToastMessage({
      type: "error",
      message: error.message,
    });
  }
};

const ProjectFilterControl = ({
  anchorEl,
  open,
  onClick,
  onClose,
  onApply,
  filtersPC,
  setFiltersPC,
  toggleArchitecture,
  toggleStatus,
  filterVehicleClass,
  setFilterVehicleClass,
  vehicleClassFilterOptions,
}) => (
  <CustomFilterV2
    anchorEl={anchorEl}
    open={open}
    widthPerColumn="250px"
    handleClick={onClick}
    handleClose={onClose}
    handleFilterButton={onApply}
    showClearAll={true}
    handleClearAll={() => onApply("discard")}
    sections={[
      {
        heading: "Sorting",
        content: (
          <Box>
            <Box sx={{ p: 2 }}>
              <CustomRadioButtonV2
                value={filtersPC.sortBy}
                onChange={(e) =>
                  setFiltersPC((prev) => ({
                    ...prev,
                    sortBy: e.target.value,
                  }))
                }
                data={[
                  { value: "created_at", label: "Created At" },
                  { value: "updated_at", label: "Updated At" },
                ]}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() =>
                  setFiltersPC((prev) => ({
                    ...prev,
                    sortOrder: "ASC",
                  }))
                }
                sx={{
                  border: 0,
                  font: "inherit",
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 0,
                  backgroundColor:
                    filtersPC?.sortOrder === "ASC"
                      ? "#26890D"
                      : "transparent",
                  color:
                    filtersPC?.sortOrder === "ASC"
                      ? "#FFFFFF"
                      : "#000000",
                }}
              >
                <img
                  src={sortUp}
                  alt="Sort Asc–Des"
                  style={{ width: 16, height: 16 }}
                />
                <Typography variant="body2" sx={{ color: "inherit" }}>
                  Sort Asc – Des
                </Typography>
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() =>
                  setFiltersPC((prev) => ({
                    ...prev,
                    sortOrder: "DESC",
                  }))
                }
                sx={{
                  border: 0,
                  font: "inherit",
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 0,
                  backgroundColor:
                    filtersPC?.sortOrder === "DESC"
                      ? "#26890D"
                      : "transparent",
                  color:
                    filtersPC?.sortOrder === "DESC"
                      ? "#FFFFFF"
                      : "#000000",
                }}
              >
                <img
                  src={sortDown}
                  alt="Sort Des–Asc"
                  style={{ width: 16, height: 16 }}
                />
                <Typography variant="body2" sx={{ color: "inherit" }}>
                  Sort Des – Asc
                </Typography>
              </Box>
            </Box>
          </Box>
        ),
      },

      {
        heading: "Filter By Architecture Type",
        content: (
          <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
            {["ALL", ...atFilter].map((item) => (
              <CustomCheckBoxV2
                key={item}
                label={
                  item.charAt(0).toUpperCase() +
                  item.slice(1).toLowerCase()
                }
                value={isArchitectureChecked(item, filtersPC)}
                onChange={(e) => {
                  const state = e.target.checked;
                  toggleArchitecture(item, state);
                }}
              />
            ))}
          </Box>
        ),
      },

      {
        heading: "Filter By Project Status",
        content: (
          <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
            {[
              "ALL",
              "ACTIVE PRE SOP",
              "ACTIVE POST SOP",
              "DRAFT",
              "DEACTIVATED",
              "COMPLETED",
            ].map((item) => (
              <CustomCheckBoxV2
                key={item}
                label={formatLabel(item)}
                value={isStatusChecked(item, filtersPC)}
                onChange={(e) => {
                  const state = e.target.checked;
                  toggleStatus(item, state);
                }}
              />
            ))}
          </Box>
        ),
      },

      {
        heading: "Vehicle Class",
        content: (
          <Box sx={{ width: "300px" }}>
            <CustomCheckboxMultiSelect
              required={false}
              txtFldStyle={{ width: "250px" }}
              selectedValues={filterVehicleClass}
              setSelectedValues={setFilterVehicleClass}
              optionsList={vehicleClassFilterOptions}
            />
          </Box>
        ),
      },
    ]}
  />
);

ProjectFilterControl.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  filtersPC: PropTypes.object.isRequired,
  setFiltersPC: PropTypes.func.isRequired,
  toggleArchitecture: PropTypes.func.isRequired,
  toggleStatus: PropTypes.func.isRequired,
  filterVehicleClass: PropTypes.array.isRequired,
  setFilterVehicleClass: PropTypes.func.isRequired,
  vehicleClassFilterOptions: PropTypes.array.isRequired,
};

const ProjectDialogs = ({
  openAddProject,
  openRecreateProject,
  disabled,
  formLayout,
  onCloseAdd,
  onCloseRecreate,
  onSubmitAdd,
  onSubmitRecreate,
}) => (
  <>
    {openAddProject && (
      <AddDialog
        disabled={disabled}
        componentMap={componentMap}
        formLayout={formLayout}
        singleColumnRows={singleColumnRows}
        open={openAddProject}
        onClose={onCloseAdd}
        title="Create Project"
        handleClickAddDialog={onSubmitAdd}
      />
    )}

    {openRecreateProject && (
      <AddDialog
        disabled={disabled}
        componentMap={componentMap}
        formLayout={formLayout}
        singleColumnRows={singleColumnRows}
        open={openRecreateProject}
        onClose={onCloseRecreate}
        title="Recreate Project"
        handleClickAddDialog={onSubmitRecreate}
      />
    )}
  </>
);

ProjectDialogs.propTypes = {
  openAddProject: PropTypes.bool.isRequired,
  openRecreateProject: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  formLayout: PropTypes.array.isRequired,
  onCloseAdd: PropTypes.func.isRequired,
  onCloseRecreate: PropTypes.func.isRequired,
  onSubmitAdd: PropTypes.func.isRequired,
  onSubmitRecreate: PropTypes.func.isRequired,
};

const useProjectFilters = () => {
  const [anchorElPCFilter, setAnchorElPCFilter] = useState(null);
  const [openPCFilter, setOpenPCFilter] = useState(false);
  const [filtersPC, setFiltersPC] = useState(getInitialFiltersPC);
  const [filtersPCAfterClick, setFiltersPCAfterClick] = useState(() =>
    buildFiltersParams({
      page_number: 1,
      page_size: 25,
      filtersPC: getInitialFiltersPC(),
      filterVehicleClass: [],
    }),
  );
  const [filterVehicleClass, setFilterVehicleClass] = useState([]);

  const handlePCFilterIconClick = (event) => {
    setAnchorElPCFilter(event.currentTarget);
    setOpenPCFilter(true);
  };

  const handlePCFilterIconClosePC = () => {
    setAnchorElPCFilter(null);
    setOpenPCFilter(false);
  };

  const toggleStatus = (item, state) => {
    console.log("itemitem", item, state);
    setFiltersPC((prev) => getNextStatusFilter(prev, item, state));
  };

  const toggleArchitecture = (item, state) => {
    console.log("---toggleArchitecture---", item, state);
    const updatedDoubleGroup = getNextArchitectureDoubleGroup(
      filtersPC.doubleGroup,
      item,
      state,
    );

    if (!updatedDoubleGroup.CENTRALIZED && !updatedDoubleGroup.ZONAL) {
      toast.error("At least one Architecture should be selected");
      return;
    }

    setFiltersPC((prev) => ({
      ...prev,
      at_all: updatedDoubleGroup.CENTRALIZED && updatedDoubleGroup.ZONAL,
      doubleGroup: updatedDoubleGroup,
    }));
  };

  const handleApplyFiltersPC = (action, pagePC, rowsPerPagePC, setPagePC) => {
    if (action === "discard") {
      const discarded = getDiscardedFiltersPC();
      setFiltersPC(discarded);
      setFilterVehicleClass([]);
      const params = buildFiltersParams({
        page_number: 1,
        page_size: rowsPerPagePC,
        filtersPC: discarded,
        filterVehicleClass: [],
      });
      setFiltersPCAfterClick(params);
      if (setPagePC) setPagePC(1);
      handlePCFilterIconClosePC();
      return;
    }

    if (action === "apply") {
      const params = buildFiltersParams({
        page_number: 1,
        page_size: rowsPerPagePC,
        filtersPC,
        filterVehicleClass,
      });
      setFiltersPCAfterClick(params);
      if (setPagePC) setPagePC(1);
      handlePCFilterIconClosePC();
      return;
    }

    handlePCFilterIconClosePC();
  };

  return {
    anchorElPCFilter,
    openPCFilter,
    filtersPC,
    setFiltersPC,
    filtersPCAfterClick,
    filterVehicleClass,
    setFilterVehicleClass,
    handlePCFilterIconClick,
    handlePCFilterIconClosePC,
    toggleStatus,
    toggleArchitecture,
    handleApplyFiltersPC,
  };
};

const useProjectInitialData = (
  dispatch,
  openAddProject,
  openRecreateProject,
) => {
  const [adminDefaultData, setAdminDefaultData] = useState({});
  const [countryData, setCountryData] = useState([]);
  const [powerTrainClassData, setPowerTrainClassData] = useState([]);
  const [fullPowerTrainClassData, setFullPowerTrainClassData] = useState([]);
  const [vehicleFunctionData, setVehicleFunctionData] = useState([]);
  const [fullVehicleFunctionData, setFullVehicleFunctionData] = useState([]);
  const [vehicleClassData, setVehicleClassData] = useState([]);
  const [usersListData, setUsersListData] = useState([]);
  const [fullUsersList, setFullUsersList] = useState([]);
  const [milestoneNameOptions, setMilestoneNameOptions] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [image, setImage] = useState([]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [
        countryRegions,
        powertrainClass,
        vehicleFunction,
        adminSettings,
        vehicleClass,
        vehicleStatus,
        usersList,
      ] = await Promise.all([
        Promise.resolve(
          dispatch(HandleApiActions({ ...profileActions.get_coutry_regions })),
        ),
        Promise.resolve(
          dispatch(
            HandleApiActions({ ...adminSettingsAPI.get_powertrain_class }),
          ),
        ),
        Promise.resolve(
          dispatch(
            HandleApiActions({ ...adminSettingsAPI.get_vehicle_function }),
          ),
        ),
        Promise.resolve(
          dispatch(
            HandleApiActions({ ...adminSettingsAPI.get_admin_settings }),
          ),
        ),
        Promise.resolve(
          dispatch(
            HandleApiActions({ ...adminSettingsAPI.get_vehicle_class }),
          ),
        ),
        Promise.resolve(
          dispatch(
            HandleApiActions({
              ...adminSettingsAPI.get_vehicle_status,
              params: {
                page_number: 1,
                page_size: 1000,
              },
            }),
          ),
        ),
        Promise.resolve(
          dispatch(
            HandleApiActions({
              ...projectConfigAPI.get_users_list,
              params: {
                page_size: 1000,
                page_number: 1,
              },
            }),
          ),
        ),
      ]);

      console.log("✅ Data fetched:", {
        countryRegions,
        powertrainClass,
        vehicleFunction,
        adminSettings,
        vehicleClass,
        usersList,
        vehicleStatus,
      });
      setCountryData(countryRegions?.data || []);
      setAdminDefaultData(adminSettings || []);
      setPowerTrainClassData(powertrainClass?.data || []);
      setFullPowerTrainClassData(powertrainClass?.data || []);
      setVehicleFunctionData(vehicleFunction?.data || []);
      setFullVehicleFunctionData(vehicleFunction?.data || []);
      setVehicleClassData(vehicleClass?.data || []);
      setUsersListData(usersList?.data || []);
      setFullUsersList(usersList?.data || []);
      setMilestoneNameOptions(
        getVehicleStatusList(vehicleStatus)
          .slice()
          .sort((a, b) => (a?.sequence || 0) - (b?.sequence || 0))
          .map((item) => ({
            label: item?.name,
            value: item?.name,
            sequence: item?.sequence,
          })),
      );
    } catch (error) {
      console.error("❌ Failed to load initial data:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData, openAddProject, openRecreateProject]);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        const metaList = await Promise.resolve(
          dispatch(HandleApiActions({ ...settingsApis.get_app_meta_data })),
        );
        setMetaData(metaList?.data || []);
      } catch (error) {
        console.error("Error fetching metadata:", error);
        setMetaData([]);
      }
    };
    void fetchMetaData();
  }, [dispatch]);

  useEffect(() => {
    if (vehicleClassData?.length) {
      fetchVehicleClassImages(vehicleClassData)
        .then(setImage)
        .catch((error) => console.error("Error fetching vehicle class images:", error));
    }
  }, [vehicleClassData]);

  return {
    adminDefaultData,
    countryData,
    powerTrainClassData,
    setPowerTrainClassData,
    fullPowerTrainClassData,
    vehicleFunctionData,
    setVehicleFunctionData,
    fullVehicleFunctionData,
    vehicleClassData,
    usersListData,
    setUsersListData,
    fullUsersList,
    milestoneNameOptions,
    metaData,
    image,
  };
};

const useFilteredDropdowns = ({
  fullUsersList,
  coOwners,
  editors,
  viewers,
  owner,
  setUsersListData,
  fullPowerTrainClassData,
  powertrainClass,
  setPowerTrainClassData,
  fullVehicleFunctionData,
  vehicleFunction,
  setVehicleFunctionData,
}) => {
  useEffect(() => {
    if (!Array.isArray(fullUsersList)) return;

    const selectedUsers = new Set(
      [...coOwners, ...editors, ...viewers, ...owner].map(
        (user) => user.value,
      ),
    );

    const unSelectedUserListData = fullUsersList.filter(
      (user) => !selectedUsers.has(user.user_id),
    );

    setUsersListData(unSelectedUserListData);
  }, [coOwners, editors, viewers, owner, fullUsersList, setUsersListData]);

  useEffect(() => {
    if (!Array.isArray(fullPowerTrainClassData)) return;

    const selectedPowerTrainClass = new Set(
      powertrainClass.map((pt) => Number(pt.value)),
    );

    const unselectedPowerTrainClass = fullPowerTrainClassData.filter(
      (pt) => !selectedPowerTrainClass.has(Number(pt.id)),
    );

    setPowerTrainClassData(unselectedPowerTrainClass);
  }, [powertrainClass, fullPowerTrainClassData, setPowerTrainClassData]);

  useEffect(() => {
    if (!Array.isArray(fullVehicleFunctionData)) return;

    const selectedVf = new Set(
      vehicleFunction.map((pt) => Number(pt.value)),
    );

    const unselectedVf = fullVehicleFunctionData.filter(
      (pt) => !selectedVf.has(Number(pt.id)),
    );

    setVehicleFunctionData(unselectedVf);
  }, [vehicleFunction, fullVehicleFunctionData, setVehicleFunctionData]);
};

function ProjectConfig() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openAddProject, setOpenAddProject] = useState(false);
  const openViewMore = false;
  const [selectCompactToDense, setSelectCompactToDense] = useState(false);

  const [openRecreateProject, setOpenRecreateProject] = useState(false);
  const [getProjectTrigger, setGetProjectTrigger] = useState(false);

  const [allProjectDetails, setAllProjectDetails] = useState([]);

  const [pagePC, setPagePC] = useState(1);
  const rowsPerPagePC = 25;

  const [projectName, setProjectName] = useState("");
  const [currentProjectID, setCurrentProjectID] = useState(null);
  const [projectStatus, setProjectStatus] = useState("");
  const [modelYear, setModelYear] = useState(null);
  const [trademark, setTrademark] = useState("");
  const [vehiclePlatform, setVehiclePlatform] = useState("");
  const [country, setCountry] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [architecture, setArchitecture] = useState("");
  const [numSeats, setNumSeats] = useState("");
  const [zoneNames, setZoneNames] = useState([]);
  const [vehicleClass, setVehicleClass] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [targetMarket, setTargetMarket] = useState("");
  const [deliveryMarket, setDeliveryMarket] = useState([]);
  const [vehicleFunction, setVehicleFunction] = useState([]);
  const [powertrainClass, setPowertrainClass] = useState([]);
  const [manufacturerDetails, setManufacturerDetails] = useState([]);
  const [owner, setOwner] = useState([]);
  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [milestoneRows, setMilestoneRows] = useState([]);
  const [milestoneDraft, setMilestoneDraft] = useState(createMilestoneRow());
  const [totalCountPC, setTotalCountPC] = useState(null);

  const {
    adminDefaultData,
    countryData,
    powerTrainClassData,
    setPowerTrainClassData,
    fullPowerTrainClassData,
    vehicleFunctionData,
    setVehicleFunctionData,
    fullVehicleFunctionData,
    vehicleClassData,
    usersListData,
    setUsersListData,
    fullUsersList,
    milestoneNameOptions,
    metaData,
    image,
  } = useProjectInitialData(dispatch, openAddProject, openRecreateProject);

  const {
    anchorElPCFilter,
    openPCFilter,
    filtersPC,
    setFiltersPC,
    filtersPCAfterClick,
    filterVehicleClass,
    setFilterVehicleClass,
    handlePCFilterIconClick,
    handlePCFilterIconClosePC,
    toggleStatus,
    toggleArchitecture,
    handleApplyFiltersPC,
  } = useProjectFilters();

  useFilteredDropdowns({
    fullUsersList,
    coOwners,
    editors,
    viewers,
    owner,
    setUsersListData,
    fullPowerTrainClassData,
    powertrainClass,
    setPowerTrainClassData,
    fullVehicleFunctionData,
    vehicleFunction,
    setVehicleFunctionData,
  });

  useEffect(() => {
    setTimeZone(getTimeZoneForCountry(country, countryData));
  }, [country, countryData]);

  useEffect(() => {
    resetDraftIfDuplicate(milestoneDraft, milestoneRows, setMilestoneDraft);
  }, [milestoneDraft, milestoneRows]);

  const formLayout = buildProjectFormLayout({
    projectName,
    setProjectName,
    projectStatus,
    setProjectStatus,
    metaData,
    modelYear,
    setModelYear,
    milestoneRows,
    setMilestoneRows,
    milestoneDraft,
    setMilestoneDraft,
    milestoneNameOptions,
    trademark,
    setTrademark,
    vehiclePlatform,
    setVehiclePlatform,
    country,
    setCountry,
    countryData,
    timeZone,
    setTimeZone,
    adminDefaultData,
    architecture,
    setArchitecture,
    numSeats,
    setNumSeats,
    zoneNames,
    setZoneNames,
    image,
    vehicleClass,
    setVehicleClass,
    vehicleType,
    setVehicleType,
    targetMarket,
    setTargetMarket,
    deliveryMarket,
    setDeliveryMarket,
    vehicleFunction,
    setVehicleFunction,
    vehicleFunctionData,
    powertrainClass,
    setPowertrainClass,
    powerTrainClassData,
    manufacturerDetails,
    setManufacturerDetails,
    coOwners,
    setCoOwners,
    usersListData,
    openRecreateProject,
    editors,
    setEditors,
    viewers,
    setViewers,
  });

  const handleProjectDetails = async (params = {}) => {
    try {
      const {
        project_id,
        name,
        trade_mark,
        status,
        model_year,
        is_zonal,
        page_number,
        page_size,
        order_by_sort,
        order_by_column,
        vehicle_class,
      } = params;

      const queryParams = {
        page_number,
        ...(page_size && { page_size }),
        ...(page_number && { page_number }),
        ...(project_id && { project_id }),
        ...(name && { name }),
        ...(trade_mark && { trade_mark }),
        ...(status && { status }),
        ...(vehicle_class && { vehicle_class }),
        ...(model_year && { model_year }),
        ...(is_zonal === undefined ? {} : { is_zonal }),
        ...(order_by_sort && { order_by_sort }),
        ...(order_by_column && { order_by_column }),
      };

      console.log("queryParamsqueryParamsqueryParams", queryParams);

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_project,
            params: queryParams,
          }),
        ),
      );

      return resp;
    } catch (error) {
      console.error("Error fetching project details:", error);
      return { data: [], total_count: 0 };
    }
  };

  const fetchAllProjects = async () => {
    try {
      const resp = await handleProjectDetails({
        page_number: pagePC,
        page_size: rowsPerPagePC,
        order_by_sort: filtersPC.sortOrder,
        order_by_column: filtersPC.sortBy,
      });
      const allProjects = resp?.data || [];
      setTotalCountPC(resp?.total_count);
      setAllProjectDetails(allProjects);
    } catch (error) {
      console.error("❌ Error fetching all projects:", error);
    }
  };

  const handlePageChange = (newPage) => {
    setPagePC(newPage);
  };

  const emptyStatePC = () => {
    resetProjectFormState({
      setProjectName,
      setCurrentProjectID,
      setProjectStatus,
      setModelYear,
      setTrademark,
      setVehiclePlatform,
      setCountry,
      setTimeZone,
      setArchitecture,
      setNumSeats,
      setZoneNames,
      setVehicleClass,
      setVehicleType,
      setTargetMarket,
      setDeliveryMarket,
      setVehicleFunction,
      setPowertrainClass,
      setManufacturerDetails,
      setCoOwners,
      setViewers,
      setEditors,
      setMilestoneRows,
      setMilestoneDraft,
    });
  };

  const handleOpenCloseAddProject = (value) =>
    closeOrOpenDialog(setOpenAddProject, value, emptyStatePC);

  const handleOpenRecreateProject = async ({ project_id }) => {
    setCurrentProjectID(project_id);
    let resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_project_recreate,
          params: { project_id: project_id },
          show_toast: false,
        }),
      ),
    );

    const parentProjectDetails = resp.data[0];
    console.log(
      "---parentCountryData---",
      parentProjectDetails?.model_year,
    );
    if (!resp.error) {
      const recreateState = buildRecreateProjectState(
        parentProjectDetails,
        countryData,
        milestoneNameOptions,
      );
      applyRecreateProjectState(
        {
          setProjectName,
          setProjectStatus,
          setModelYear,
          setTrademark,
          setVehiclePlatform,
          setCountry,
          setTimeZone,
          setArchitecture,
          setNumSeats,
          setZoneNames,
          setVehicleClass,
          setVehicleType,
          setTargetMarket,
          setDeliveryMarket,
          setVehicleFunction,
          setPowertrainClass,
          setManufacturerDetails,
          setOwner,
          setCoOwners,
          setEditors,
          setViewers,
          setMilestoneRows,
          setMilestoneDraft,
        },
        recreateState,
      );
    }

    setOpenRecreateProject(true);
  };

  const handleCloseRecreateProject = (value) =>
    closeOrOpenDialog(setOpenRecreateProject, value, emptyStatePC);

  const handleOpenCloseViewMore = (value, item = null) => {
    navigateToProjectConfig(navigate, value, item, {
      adminDefaultData,
      powerTrainClassData,
      vehicleFunctionData,
      metaData,
    });
  };

  const buildMilestonesPayload = () =>
    buildMilestonesPayloadFromRows(milestoneRows, milestoneNameOptions);

  const handleClickAddDialogProject = async () => {
    const reqObj = buildProjectRequestPayload({
      projectName,
      trademark,
      vehiclePlatform,
      projectStatus,
      architecture,
      manufacturerDetails,
      zoneNames,
      vehicleClass,
      powertrainClass,
      vehicleFunction,
      country,
      timeZone,
      deliveryMarket,
      coOwners,
      targetMarket,
      vehicleType,
      numSeats,
      modelYear,
      milestones: buildMilestonesPayload(),
      includeEditorsViewers: false,
    });

    console.log("reqObj...", reqObj);
    let resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.add_project,
          params: reqObj,
          show_toast: true,
        }),
      ),
    );
    if (!resp.error) {
      await fetchAllProjects();
      handleOpenCloseAddProject(false);
    }

    console.log("🧾 Project Request Object:", reqObj);
  };

  const handleClickRecreateDialogProject = async () => {
    const reqObj = buildProjectRequestPayload({
      projectName,
      currentProjectID,
      trademark,
      vehiclePlatform,
      projectStatus,
      architecture,
      manufacturerDetails,
      zoneNames,
      vehicleClass,
      powertrainClass,
      vehicleFunction,
      country,
      timeZone,
      deliveryMarket,
      coOwners,
      editors,
      viewers,
      targetMarket,
      vehicleType,
      numSeats,
      modelYear,
      milestones: buildMilestonesPayload(),
      includeEditorsViewers: true,
    });

    console.log("reqObj...", reqObj);
    let resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...projectConfigAPI.add_project_recreate,
          params: reqObj,
          show_toast: true,
        }),
      ),
    );
    if (!resp.error) {
      setGetProjectTrigger(!getProjectTrigger);
      handleCloseRecreateProject(false);
    }

    console.log("🧾 Project Request Object:", reqObj);
  };

  const isAddDisabledPC = checkIsAddDisabled({
    projectName,
    projectStatus,
    modelYear,
    architecture,
    vehicleClass,
    vehicleType,
    country,
    timeZone,
    vehiclePlatform,
    targetMarket,
    numSeats,
    manufacturerDetails,
    powertrainClass,
    milestoneRows,
    milestoneNameOptions,
  });

  const handleImportedJsonExcelFile = (payload) => {
    console.log("---importedFileData---", payload?.importedFileData);
    handleImportedFile({
      ...payload,
      dispatch,
      onSuccess: fetchAllProjects,
    });
  };

  console.log("fullUsersListfullUsersListfullUsersList", fullUsersList);

  const vehicleClassFilterOptions = useMemo(
    () => getVehicleClassFilterOptions(vehicleClassData),
    [vehicleClassData],
  );

  const searchParams = useMemo(
    () => ({
      page_number: pagePC,
      page_size: rowsPerPagePC,
      order_by_sort:
        filtersPCAfterClick?.order_by_sort ||
        filtersPCAfterClick?.sortOrder ||
        "DESC",
      order_by_column:
        filtersPCAfterClick?.order_by_column ||
        filtersPCAfterClick?.sortBy ||
        "created_at",
      ...(filtersPCAfterClick?.status && {
        status: filtersPCAfterClick.status,
      }),
      ...(filtersPCAfterClick?.vehicle_class && {
        vehicle_class: filtersPCAfterClick.vehicle_class,
      }),
      ...(filtersPCAfterClick?.is_zonal !== undefined && {
        is_zonal: filtersPCAfterClick.is_zonal,
      }),
    }),
    [pagePC, rowsPerPagePC, filtersPCAfterClick],
  );

  return (
    <div>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box>
              <ProjectFilterControl
                anchorEl={anchorElPCFilter}
                open={openPCFilter}
                onClick={handlePCFilterIconClick}
                onClose={handlePCFilterIconClosePC}
                onApply={(action) =>
                  handleApplyFiltersPC(action, pagePC, rowsPerPagePC, setPagePC)
                }
                filtersPC={filtersPC}
                setFiltersPC={setFiltersPC}
                toggleArchitecture={toggleArchitecture}
                toggleStatus={toggleStatus}
                filterVehicleClass={filterVehicleClass}
                setFilterVehicleClass={setFilterVehicleClass}
                vehicleClassFilterOptions={vehicleClassFilterOptions}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box>
                <CustomSearchbarWithApiV2
                  apiAction={projectConfigAPI.get_project}
                  triggerApi={getProjectTrigger}
                  params={searchParams}
                  skipInitialSearch={false}
                  searchKey="name"
                  size="small"
                  type="text"
                  variant="outlined"
                  placeholder="Search Projects"
                  valueError={""}
                  onSearchQueryChange={(newSearch, oldSearch) => {
                    console.log("----old vs new---", newSearch, oldSearch);
                  }}
                  isMultiline={false}
                  minRows={1}
                  maxRows={3}
                  disabled={false}
                  txtFldStyle={{
                    width: "100%",
                  }}
                  getDataOnSearch={(data, resp) => {
                    console.log("Search datadata", data);
                    setTotalCountPC(resp?.total_count);
                    setAllProjectDetails(data);
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small"></IconButton>
                <Tooltip
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#000", // pure black tooltip
                        color: "black",
                        borderRadius: 0,
                        p: 1.5,
                        "& .MuiTooltip-arrow": {
                          color: "#000",
                        },
                      },
                    },
                  }}
                  title={
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: "#fff", // white box
                            border: "1px solid #ccc",
                            borderRadius: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontSize: "0.85rem" }}
                        >
                          <b>Draft Project</b>
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: "#888", // grey box
                            borderRadius: "2px",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontSize: "0.85rem" }}
                        >
                          <b>Completed Project</b>
                        </Typography>
                      </Box>
                    </Box>
                  }
                >
                </Tooltip>
              </Box>
            </Box>
            <Box
              sx={{ ml: -3 }}
              onClick={() => setSelectCompactToDense(!selectCompactToDense)}
            >
              {selectCompactToDense ? (
                <IconButton
                  size="small"
                  onClick={() => setSelectCompactToDense(false)}
                  sx={{
                    color: "black",
                    filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))",
                  }}
                >
                  <ListIcon />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => setSelectCompactToDense(true)}
                  sx={{
                    color: "black",
                    filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))",
                  }}
                >
                  <GridIcon />
                </IconButton>
              )}
            </Box>
            <Box sx={{ ml: -2 }}>
              <CustomImportButton
                handleImportedJsonExcelFile={handleImportedJsonExcelFile}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box>
              <AddButtonV2
                handleOnClick={() => handleOpenCloseAddProject(true)}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: 0.5,
            minHeight: "70vh",
            maxHeight: "75vh",
            overflowY: "auto",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-thumb": { borderRadius: "4px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            },
          }}
        >
          {selectCompactToDense ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
              }}
            >
              <ProjectGridView
                isDense={true}
                projects={allProjectDetails}
                onRecreate={(projectId) => {
                  void handleOpenRecreateProject({
                    project_id: projectId,
                  });
                }}
                onViewMore={(item) => handleOpenCloseViewMore(true, item)}
              />
            </Box>
          ) : (
            <ProjectGridView
              isDense={false}
              projects={allProjectDetails}
              onRecreate={(projectId) => {
                void handleOpenRecreateProject({
                  project_id: projectId,
                });
              }}
              onViewMore={(item) => handleOpenCloseViewMore(true, item)}
            />
          )}
        </Box>
        {console.log("allProjectDetailsallProjectDetails", allProjectDetails)}

        {Array.isArray(allProjectDetails) && allProjectDetails.length > 0 ? (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              p: 2,
              zIndex: 999,
            }}
          >
            <CustomPaginationV2
              page={pagePC}
              rowsPerPage={rowsPerPagePC}
              totalItems={totalCountPC}
              onPageChange={handlePageChange}
            />
          </Box>
        ) : null}
      </Box>
      <ProjectDialogs
        openAddProject={openAddProject}
        openRecreateProject={openRecreateProject}
        disabled={isAddDisabledPC}
        formLayout={formLayout}
        onCloseAdd={() => handleOpenCloseAddProject(false)}
        onCloseRecreate={() => handleCloseRecreateProject(false)}
        onSubmitAdd={handleClickAddDialogProject}
        onSubmitRecreate={handleClickRecreateDialogProject}
      />
      {openViewMore && (
        <ViewProjectConfig
          onViewMore={() => handleOpenCloseViewMore(false, null)}
        />
      )}
    </div>
  );
}

export default ProjectConfig;
